import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import useGlobalContext from "./useGlobalContext.js";
import { flightsLocalStore } from "../utils/local-store.js";
import useClientMetrics from "./useClientMetrics.js";
import { getSearchResultsFlights } from "../store/searchResults/selectors.js";
import { getFlight } from "../store/flightDetails/selectors.js";
export default function useReportBaggageQuality(token) {
    const LOCALSTORAGEKEY = "fd_v_sr_baggage_info_quality_check";
    const searchResultsFlights = useSelector(getSearchResultsFlights);
    const flightDetailsFlight = useSelector(getFlight);
    const { features } = useGlobalContext();
    const { trackClientMetric } = useClientMetrics();
    const flightSR = useMemo(() => {
        return searchResultsFlights?.find((flight) => flight?.token == token);
    }, [token, searchResultsFlights]);
    const isReported = useCallback((token) => {
        if (!flightsLocalStore)
            return false;
        const data = flightsLocalStore.get(LOCALSTORAGEKEY) || [];
        return data.some((k) => k === token);
    }, []);
    const setIsReported = useCallback((token) => {
        if (!flightsLocalStore)
            return;
        const data = flightsLocalStore.get(LOCALSTORAGEKEY) || [];
        data.push(token);
        flightsLocalStore.set(LOCALSTORAGEKEY, data);
    }, []);
    const reportPersonalItem = useCallback((srItem, fdItem) => {
        if (srItem !== undefined && fdItem !== undefined) {
            trackClientMetric("fd_v_sr_baggage_info_personal_item_available_match", 1);
        }
        if (srItem !== undefined && fdItem === undefined) {
            trackClientMetric("fd_v_sr_baggage_info_personal_item_available_mismatch", 1);
        }
        if (srItem === undefined && fdItem === undefined) {
            trackClientMetric("fd_v_sr_baggage_info_personal_item_not_available_match", 1);
        }
        if (srItem === undefined && fdItem !== undefined) {
            trackClientMetric("fd_v_sr_baggage_info_personal_item_not_available_mismatch", 1);
        }
    }, [trackClientMetric]);
    const reportCabinBag = useCallback((srItem, fdItem) => {
        if (srItem !== undefined && fdItem !== undefined) {
            trackClientMetric("fd_v_sr_baggage_info_cabin_available_match", 1);
        }
        if (srItem !== undefined && fdItem === undefined) {
            trackClientMetric("fd_v_sr_baggage_info_cabin_available_mismatch", 1);
        }
        if (srItem === undefined && fdItem === undefined) {
            trackClientMetric("fd_v_sr_baggage_info_cabin_not_available_match", 1);
        }
        if (srItem === undefined && fdItem !== undefined) {
            trackClientMetric("fd_v_sr_baggage_info_cabin_not_available_mismatch", 1);
        }
        if (!!srItem?.maxWeightPerPiece) {
            const isEqualMaxWeight = srItem.maxWeightPerPiece === fdItem?.maxWeightPerPiece;
            if (isEqualMaxWeight)
                trackClientMetric("fd_v_sr_baggage_info_cabin_max_weight_match", 1);
            if (!isEqualMaxWeight)
                trackClientMetric("fd_v_sr_baggage_info_cabin_max_weight_mismatch", 1);
        }
        if (!!srItem?.maxWeightPerPiece) {
            const isEqualMaxPiece = srItem.maxPiece === fdItem?.maxPiece;
            if (isEqualMaxPiece)
                trackClientMetric("fd_v_sr_baggage_info_cabin_max_piece_match", 1);
            if (!isEqualMaxPiece)
                trackClientMetric("fd_v_sr_baggage_info_cabin_max_piece_mismatch", 1);
        }
        const srSizeRestrictions = srItem?.sizeRestrictions;
        const fdSizeRestrictions = fdItem?.sizeRestrictions;
        if (!!srSizeRestrictions?.maxHeight && !!srSizeRestrictions?.maxLength && !!srSizeRestrictions?.maxWidth) {
            const isEqualDimension = srSizeRestrictions.maxHeight === fdSizeRestrictions?.maxHeight &&
                srSizeRestrictions.maxLength === fdSizeRestrictions?.maxLength &&
                srSizeRestrictions.maxWidth === fdSizeRestrictions?.maxWidth;
            if (isEqualDimension)
                trackClientMetric("fd_v_sr_baggage_info_cabin_dimensions_match", 1);
            if (!isEqualDimension)
                trackClientMetric("fd_v_sr_baggage_info_cabin_dimensions_mismatch", 1);
        }
    }, [trackClientMetric]);
    const reportCheckedBag = useCallback((srItem, fdItem) => {
        if (srItem !== undefined && fdItem !== undefined) {
            trackClientMetric("fd_v_sr_baggage_info_checked_available_match", 1);
        }
        if (srItem !== undefined && fdItem === undefined) {
            trackClientMetric("fd_v_sr_baggage_info_checked_available_mismatch", 1);
        }
        if (srItem === undefined && fdItem === undefined) {
            trackClientMetric("fd_v_sr_baggage_info_checked_not_available_match", 1);
        }
        if (srItem === undefined && fdItem !== undefined) {
            trackClientMetric("fd_v_sr_baggage_info_checked_not_available_mismatch", 1);
        }
        if (!!srItem?.maxWeightPerPiece) {
            const isEqualMaxWeight = srItem.maxWeightPerPiece === fdItem?.maxWeightPerPiece;
            if (isEqualMaxWeight)
                trackClientMetric("fd_v_sr_baggage_info_checked_max_weight_match", 1);
            if (!isEqualMaxWeight)
                trackClientMetric("fd_v_sr_baggage_info_checked_max_weight_mismatch", 1);
        }
    }, [trackClientMetric]);
    const reportCabinBagPerTraveller = useCallback((srItem, fdItem) => {
        if (srItem !== undefined && fdItem !== undefined) {
            trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_available_match", 1);
        }
        if (srItem !== undefined && fdItem === undefined) {
            trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_available_mismatch", 1);
        }
        if (srItem === undefined && fdItem === undefined) {
            trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_not_available_match", 1);
        }
        if (srItem === undefined && fdItem !== undefined) {
            trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_not_available_mismatch", 1);
        }
        if (!!srItem?.luggageAllowance?.maxWeightPerPiece) {
            const isEqualMaxWeight = srItem.luggageAllowance.maxWeightPerPiece === fdItem?.luggageAllowance?.maxWeightPerPiece;
            if (isEqualMaxWeight)
                trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_max_weight_match", 1);
            if (!isEqualMaxWeight)
                trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_max_weight_mismatch", 1);
        }
        if (!!srItem?.luggageAllowance?.maxPiece) {
            const isEqualMaxPiece = srItem.luggageAllowance.maxPiece === fdItem?.luggageAllowance?.maxPiece;
            if (isEqualMaxPiece)
                trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_max_piece_match", 1);
            if (!isEqualMaxPiece)
                trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_max_piece_mismatch", 1);
        }
        if (srItem?.luggageAllowance?.luggageType === "HAND") {
            const srSizeRestrictions = srItem?.luggageAllowance?.sizeRestrictions;
            const fdSizeRestrictions = fdItem?.luggageAllowance?.["sizeRestrictions"];
            if (!!srSizeRestrictions?.maxHeight && !!srSizeRestrictions?.maxLength && !!srSizeRestrictions?.maxWidth) {
                const isEqualDimension = srSizeRestrictions.maxHeight === fdSizeRestrictions?.maxHeight &&
                    srSizeRestrictions.maxLength === fdSizeRestrictions?.maxLength &&
                    srSizeRestrictions.maxWidth === fdSizeRestrictions?.maxWidth;
                if (isEqualDimension)
                    trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_dimensions_match", 1);
                if (!isEqualDimension)
                    trackClientMetric("fd_v_sr_baggage_info_traveller_cabin_dimensions_match", 1);
            }
        }
    }, [trackClientMetric]);
    const reportCheckedInBag = useCallback((srItem, fdItem) => {
        if (srItem?.options !== undefined && fdItem?.options !== undefined) {
            trackClientMetric("fd_v_sr_baggage_info_checked_in_available_match", 1);
        }
        if (srItem?.options !== undefined && fdItem?.options === undefined) {
            trackClientMetric("fd_v_sr_baggage_info_checked_in_available_mismatch", 1);
        }
        if (srItem?.options === undefined && fdItem?.options === undefined) {
            trackClientMetric("fd_v_sr_baggage_info_checked_in_not_available_match", 1);
        }
        if (srItem?.options === undefined && fdItem?.options !== undefined) {
            trackClientMetric("fd_v_sr_baggage_info_checked_in_not_available_mismatch", 1);
        }
        srItem?.options?.forEach((srOption, idx) => {
            const fdOption = fdItem?.options?.[idx];
            if (!!srOption?.luggageAllowance?.maxWeightPerPiece) {
                const isEqualMaxWeight = srOption.luggageAllowance.maxWeightPerPiece === fdOption?.luggageAllowance?.maxWeightPerPiece;
                if (isEqualMaxWeight)
                    trackClientMetric("fd_v_sr_baggage_info_checked_in_max_weight_match", 1);
                if (!isEqualMaxWeight)
                    trackClientMetric("fd_v_sr_baggage_info_checked_in_max_weight_mismatch", 1);
                const isEqualMaxPiece = srOption.luggageAllowance.maxPiece === fdOption?.luggageAllowance?.maxPiece;
                if (isEqualMaxPiece)
                    trackClientMetric("fd_v_sr_baggage_info_checked_in_max_piece_match", 1);
                if (!isEqualMaxPiece)
                    trackClientMetric("fd_v_sr_baggage_info_checked_in_max_piece_mismatch", 1);
            }
        });
    }, [trackClientMetric]);
    const reportBaggageQuality = useCallback((flightSR, flightFD) => {
        flightFD?.includedProducts?.segments.forEach((segmentFD, idx) => {
            const segmentSR = flightSR.includedProducts?.segments[idx];
            const checkedInFD = segmentFD.find((segment) => segment.luggageType === "CHECKED_IN");
            const checkedInSR = segmentSR?.find((segment) => segment.luggageType === "CHECKED_IN");
            reportCheckedBag(checkedInSR, checkedInFD);
            const handFD = segmentFD.find((segment) => segment.luggageType === "HAND");
            const handSR = segmentSR?.find((segment) => segment.luggageType === "HAND");
            reportCabinBag(handSR, handFD);
            const personalItemFD = segmentFD.find((segment) => segment.luggageType === "PERSONAL_ITEM");
            const personalItemSR = segmentSR?.find((segment) => segment.luggageType === "PERSONAL_ITEM");
            reportPersonalItem(personalItemSR, personalItemFD);
        });
        const cabinBaggagePerTravellerFD = flightFD?.ancillaries?.cabinBaggagePerTraveller;
        const cabinBaggagePerTravellerSR = flightSR?.ancillaries?.cabinBaggagePerTraveller;
        reportCabinBagPerTraveller(cabinBaggagePerTravellerSR, cabinBaggagePerTravellerFD);
        const checkedInBaggageFD = flightFD?.ancillaries?.checkedInBaggage;
        const checkedInBaggageSR = flightSR?.ancillaries?.checkedInBaggage;
        reportCheckedInBag(checkedInBaggageSR, checkedInBaggageFD);
        setIsReported(token);
    }, [
        token,
        setIsReported,
        reportPersonalItem,
        reportCabinBag,
        reportCheckedBag,
        reportCabinBagPerTraveller,
        reportCheckedInBag
    ]);
    useEffect(() => {
        if (!features.FLIGHTS_WEB_REPORT_BAGGAGE_QUALITY)
            return;
        if (!token || isReported(token))
            return;
        const flightFD = flightDetailsFlight;
        if (flightSR === undefined || flightFD === undefined)
            return;
        reportBaggageQuality(flightSR, flightFD);
    }, [token, flightSR, flightDetailsFlight, features, isReported, reportBaggageQuality]);
    return { reportBaggageQuality };
}
